import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { HrActivity, HrSchedule, HrSlot, hrScheduleActivityId } from '@app/shared/_models/hr';
import { Store } from '@app/shared/_models/common';
import { addDays, hoursLabel, isoWeekOf, minutesLabel, mondayOf, scheduleAppliesOn, weekIndexOf } from '../../hr-schedule-engine';
import { HR_DAY_LABELS } from '../schedule.component';
import { todayStr } from '@app/shared/time';
import { isPlainLeftClick } from '@app/shared/util';

export interface PersonPlanningDialogData {
	id_employee: number;
	name: string;
	color: string;
	contract_min: number | null;
	schedules: HrSchedule[];
	activities: HrActivity[];
	stores: Store[];
	// État ÉDITÉ (non enregistré) du planning ouvert dans l'éditeur : prime sur la version serveur.
	pendingSlots: HrSlot[];
	pendingScheduleId: number;
	pendingAnchor: string;
	pendingCycle: number;
}

interface DayEntry {
	time: string;				// « 08:00–12:30 »
	place: string;			// « Ollioules — Magasin »
	here: boolean;			// créneau du planning ouvert dans l'éditeur
}

interface DayVM {
	label: string;			// « Lundi 20/07 »
	entries: DayEntry[];
}

// Le planning d'UNE personne, semaine réelle par semaine réelle, TOUS services et établissements
// confondus (« le planning dans l'autre sens ») — horaires types uniquement (pas les exceptions :
// pour le réel, voir /hr/planning). Chaque planning est résolu sur SON cycle pour la semaine affichée.
@Component({
	selector: 'app-hr-person-planning-dialog',
	standalone: true,
	imports: [RouterLink, MatDialogModule, MatButtonModule, MatIconModule, MatTooltipModule],
	templateUrl: './person-planning-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './person-planning-dialog.component.scss',
})
export class PersonPlanningDialogComponent {

	public data: PersonPlanningDialogData = inject(MAT_DIALOG_DATA);
	private dialogRef = inject(MatDialogRef<PersonPlanningDialogComponent>);

	public monday = mondayOf(todayStr());
	public weekLabel = '';
	public days: DayVM[] = [];
	public totalLabel = '';

	constructor() {
		this.build();
	}

	// Le dialogue se referme quand la fiche s'ouvre DANS l'onglet courant : sinon il resterait
	// posé par-dessus la fiche. Ctrl/shift+clic ouvre un onglet à côté : on ne touche à rien.
	onSheetLink(e: MouseEvent): void {
		if (isPlainLeftClick(e)) this.dialogRef.close();
	}

	onShiftWeek(delta: number): void {
		this.monday = addDays(this.monday, delta * 7);
		this.build();
	}

	onToday(): void {
		this.monday = mondayOf(todayStr());
		this.build();
	}

	private build(): void {
		const to = addDays(this.monday, 6);
		this.weekLabel = 'S' + isoWeekOf(this.monday) + ' — du ' + this.fmt(this.monday) + ' au ' + this.fmt(to);

		let totalMin = 0;
		let hereMin = 0;
		this.days = [0, 1, 2, 3, 4, 5, 6].map(i => {
			const weekday = i + 1;
			const entries: DayEntry[] = [];
			const dat = addDays(this.monday, i);
			for (const sch of this.data.schedules) {
				const here = sch.id === this.data.pendingScheduleId;
				// La grille en cours d'édition reste toujours prévisualisée ; les AUTRES respectent leur fenêtre de validité.
				if (here ? sch.is_active === false : !scheduleAppliesOn(sch, dat)) continue;
				const anchor = here ? this.data.pendingAnchor : sch.anchor_monday;
				const cycle = here ? this.data.pendingCycle : sch.cycle_weeks;
				const slots = (here ? this.data.pendingSlots : (sch.lnk_slots ?? []))
					.filter(s => s.id_employee === this.data.id_employee
						&& s.week_index === weekIndexOf(this.monday, anchor, cycle)
						&& s.weekday === weekday)
					.sort((a, b) => a.start_min - b.start_min);
				for (const s of slots) {
					const minutes = Math.max(0, s.end_min - s.start_min);
					totalMin += minutes;
					if (here) hereMin += minutes;
					entries.push({
						time: minutesLabel(s.start_min) + '–' + minutesLabel(s.end_min),
						place: (this.data.stores.find(st => st.id === sch.id_store)?.name ?? '')
							+ ' — ' + (this.data.activities.find(a => a.id === hrScheduleActivityId(sch))?.name ?? sch.name),
						here,
					});
				}
			}
			entries.sort((a, b) => a.time.localeCompare(b.time));
			return { label: HR_DAY_LABELS[i] + ' ' + this.fmt(addDays(this.monday, i)), entries };
		});

		this.totalLabel = hoursLabel(totalMin)
			+ (this.data.contract_min != null ? ' / ' + hoursLabel(this.data.contract_min) + ' au contrat' : '')
			+ (hereMin > 0 && hereMin < totalMin ? ' — dont ' + hoursLabel(hereMin) + ' ici' : '');
	}

	private fmt(dat: string): string {
		return dat.slice(8, 10) + '/' + dat.slice(5, 7);
	}
}
